<template>
  <section class="admin-content" id="executive-summary">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between flex-wrap text-white" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-briefcase" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">Executive Summary</h4>
                <small style="opacity: 0.8;">Emailed to the Executive Secretary and Directors every Monday at 6:00</small>
              </div>
            </div>
            <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
              <select class="form-control form-control-sm" style="width: auto;" v-model="preset" @change="applyPreset">
                <option value="last_week">Last week</option>
                <option value="this_week">This week so far</option>
                <option value="last_month">Last month</option>
                <option value="custom">Custom dates</option>
              </select>
              <template v-if="preset === 'custom'">
                <input type="date" class="form-control form-control-sm" style="width: auto;" v-model="from" />
                <input type="date" class="form-control form-control-sm" style="width: auto;" v-model="to" />
                <button class="btn filter-toggle-btn" @click="load"><i class="fe fe-refresh-cw mr-1"></i> Load</button>
              </template>
              <button v-if="canSend" class="btn filter-toggle-btn" @click="send" :disabled="sending">
                <i class="fe fe-send mr-1"></i> {{ sending ? "Sending…" : "Send now" }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="loading" class="card shadow-sm table-card"><div class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div></div>
          <div v-else-if="error" class="card shadow-sm table-card"><div class="empty-state"><i class="fe fe-lock"></i>{{ error }}</div></div>
          <template v-else-if="s">
            <p class="text-muted small mb-3">{{ s.agency.name }} · {{ s.period.from }} to {{ s.period.to }}</p>

            <div class="row">
              <div class="col-lg-4 col-md-6" v-for="tile in tiles" :key="tile.label">
                <div class="stat-card">
                  <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
                  <div><p class="stat-label">{{ tile.label }}</p><h3 class="stat-value">{{ tile.value }}</h3><p class="stat-sub">{{ tile.sub }}</p></div>
                </div>
              </div>
            </div>

            <!-- Enrolment -->
            <div class="card shadow-sm table-card mb-4">
              <div class="form-card-header">
                <div class="section-number">1</div>
                <h6 class="mb-0 font-weight-bold text-white">Enrolment by programme</h6>
              </div>
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-head">
                    <tr><th class="table-th">Programme</th><th class="table-th text-right">Principals</th><th class="table-th text-right">Dependants</th><th class="table-th text-right">Total</th><th class="table-th text-right">New in period</th><th class="table-th text-right">Expired principals</th></tr>
                  </thead>
                  <tbody>
                    <tr class="table-row" v-for="r in s.enrolment.by_programme" :key="r.programme">
                      <td>{{ r.programme }}</td>
                      <td class="text-right">{{ r.principals | numeral(0, 0) }}</td>
                      <td class="text-right">{{ r.dependants | numeral(0, 0) }}</td>
                      <td class="text-right"><b>{{ r.total | numeral(0, 0) }}</b></td>
                      <td class="text-right">{{ (r.new_principals + r.new_dependants) | numeral(0, 0) }}</td>
                      <td class="text-right">{{ r.expired_principals | numeral(0, 0) }}</td>
                    </tr>
                    <tr class="table-row font-weight-bold" style="background: #f8fafc;">
                      <td>Total</td>
                      <td class="text-right">{{ e.principals | numeral(0, 0) }}</td>
                      <td class="text-right">{{ e.dependants | numeral(0, 0) }}</td>
                      <td class="text-right">{{ e.total | numeral(0, 0) }}</td>
                      <td class="text-right">{{ e.new_total | numeral(0, 0) }}</td>
                      <td class="text-right">{{ e.expired_principals | numeral(0, 0) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="row">
              <!-- Claims -->
              <div class="col-lg-6">
                <div class="card shadow-sm table-card mb-4">
                  <div class="form-card-header">
                    <div class="section-number">2</div>
                    <div><h6 class="mb-0 font-weight-bold text-white">Claims</h6>
                      <small style="color: rgba(255,255,255,.75);">{{ s.claims.submitted.count | numeral(0, 0) }} submitted ({{ money(s.claims.submitted.amount) }}) · {{ s.claims.awaiting_vetting_now | numeral(0, 0) }} waiting for vetting now</small></div>
                  </div>
                  <div class="table-responsive">
                    <table class="table mb-0">
                      <thead class="table-head"><tr><th class="table-th">Stage</th><th class="table-th text-right">Claims</th><th class="table-th text-right">Amount</th><th class="table-th">By</th></tr></thead>
                      <tbody>
                        <tr class="table-row" v-for="stage in claimStages" :key="stage.key">
                          <td>{{ stage.label }}</td>
                          <td class="text-right">{{ s.claims[stage.key].count | numeral(0, 0) }}</td>
                          <td class="text-right">{{ s.claims[stage.key].amount === undefined ? "" : money(s.claims[stage.key].amount) }}</td>
                          <td class="small">
                            <span v-for="(p, i) in (s.claims[stage.key].by || []).slice(0, 4)" :key="p.user_id">{{ i ? ", " : "" }}{{ p.name }} ({{ p.count }})</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Authorization codes -->
              <div class="col-lg-6">
                <div class="card shadow-sm table-card mb-4">
                  <div class="form-card-header">
                    <div class="section-number">3</div>
                    <div><h6 class="mb-0 font-weight-bold text-white">Authorization codes</h6>
                      <small style="color: rgba(255,255,255,.75);">{{ a.requested }} requested ({{ a.requested_corridor }} cross-state) · {{ a.approved }} approved · {{ a.rejected }} declined · {{ a.pending_now }} pending now<span v-if="a.avg_minutes_to_decision !== null"> · average {{ minutes(a.avg_minutes_to_decision) }}</span></small></div>
                  </div>
                  <div class="table-responsive">
                    <table class="table mb-0">
                      <thead class="table-head"><tr><th class="table-th">Treated by</th><th class="table-th text-right">Approved</th><th class="table-th text-right">Declined</th><th class="table-th text-right">Average time</th></tr></thead>
                      <tbody>
                        <tr v-if="!a.by.length"><td colspan="4" class="empty-state">No authorization codes were treated in this period.</td></tr>
                        <tr class="table-row" v-for="p in a.by" :key="p.user_id">
                          <td>{{ p.name }}<div class="small text-muted">{{ p.job_title }}</div></td>
                          <td class="text-right">{{ p.approved }}</td>
                          <td class="text-right">{{ p.rejected }}</td>
                          <td class="text-right">{{ p.avg_minutes === null ? "—" : minutes(p.avg_minutes) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Staff -->
            <div class="card shadow-sm table-card mb-4">
              <div class="form-card-header">
                <div class="section-number">4</div>
                <div><h6 class="mb-0 font-weight-bold text-white">Staff activity by job title</h6>
                  <small style="color: rgba(255,255,255,.75);">{{ s.staff.active_staff }} of {{ s.staff.total_staff }} staff recorded activity · tickets: {{ s.tickets.raised }} raised, {{ s.tickets.resolved }} resolved, {{ s.tickets.open_now }} open · change of facility: {{ s.change_of_facility.requested }} requested, {{ s.change_of_facility.approved }} approved</small></div>
              </div>
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-head">
                    <tr><th class="table-th">Staff</th><th class="table-th text-right">Enrolled</th><th class="table-th text-right">Vetted</th><th class="table-th text-right">Verified</th><th class="table-th text-right">Approved</th><th class="table-th text-right">Codes</th><th class="table-th text-right">COF</th><th class="table-th text-right">Tickets raised</th><th class="table-th text-right">Tickets answered</th></tr>
                  </thead>
                  <tbody>
                    <tr v-if="!s.staff.groups.length"><td colspan="9" class="empty-state">No staff activity recorded in this period.</td></tr>
                    <template v-for="g in s.staff.groups">
                      <tr :key="'g' + g.job_title" style="background: #f1f5f9;">
                        <td><b>{{ g.job_title }}</b> <span class="text-muted small">({{ g.staff.length }})</span></td>
                        <td class="text-right"><b>{{ g.totals.enrolled_total }}</b></td><td class="text-right"><b>{{ g.totals.claims_vetted }}</b></td>
                        <td class="text-right"><b>{{ g.totals.claims_verified }}</b></td><td class="text-right"><b>{{ g.totals.claims_approved }}</b></td>
                        <td class="text-right"><b>{{ g.totals.codes_treated }}</b></td><td class="text-right"><b>{{ g.totals.change_of_facility }}</b></td>
                        <td class="text-right"><b>{{ g.totals.tickets_raised }}</b></td><td class="text-right"><b>{{ g.totals.tickets_answered }}</b></td>
                      </tr>
                      <tr class="table-row" v-for="p in g.staff" :key="'p' + p.user_id">
                        <td class="pl-4"><router-link :to="`/employee/${p.user_id}`">{{ p.name }}</router-link>
                          <span v-if="p.enrolled_total" class="small text-muted"> · {{ p.enrolled_principals }} principals, {{ p.enrolled_dependants }} dependants</span></td>
                        <td class="text-right">{{ p.enrolled_total }}</td><td class="text-right">{{ p.claims_vetted }}</td>
                        <td class="text-right">{{ p.claims_verified }}</td><td class="text-right">{{ p.claims_approved }}</td>
                        <td class="text-right">{{ p.codes_treated }}</td><td class="text-right">{{ p.change_of_facility }}</td>
                        <td class="text-right">{{ p.tickets_raised }}</td><td class="text-right">{{ p.tickets_answered }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Facilities -->
            <div class="card shadow-sm table-card mb-4">
              <div class="form-card-header">
                <div class="section-number">5</div>
                <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">Facility submissions</h6>
                  <small style="color: rgba(255,255,255,.75);">{{ f.facilities_submitting }} of {{ f.facilities_total }} facilities submitted · {{ f.totals.encounters }} encounters, {{ f.totals.authorization_codes }} codes, {{ f.totals.claims }} claims, {{ f.totals.quality_assurance_reports }} QA reports</small></div>
                <input class="form-control form-control-sm" style="max-width: 220px;" placeholder="Search facility…" v-model="facilitySearch" />
              </div>
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-head"><tr><th class="table-th">Facility</th><th class="table-th text-right">Encounters</th><th class="table-th text-right">Auth codes</th><th class="table-th text-right">Claims</th><th class="table-th text-right">QA reports</th><th class="table-th text-right">Total</th></tr></thead>
                  <tbody>
                    <tr v-if="!facilityRows.length"><td colspan="6" class="empty-state">No facility submitted anything in this period.</td></tr>
                    <tr class="table-row" v-for="r in facilityRows" :key="r.facility_id">
                      <td>{{ r.name }}</td><td class="text-right">{{ r.encounters }}</td><td class="text-right">{{ r.authorization_codes }}</td>
                      <td class="text-right">{{ r.claims }}</td><td class="text-right">{{ r.quality_assurance_reports }}</td><td class="text-right"><b>{{ r.total }}</b></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import moment from "moment";

/** Weekly executive summary (GET reports/executive-summary; admins: POST reports/executive-summary/send). */
export default {
  components: { Navbar },
  data() {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    return {
      isAdmin: user.type === "shis" || String(user.user_role) === "1",
      // admins, the Executive Secretary and Directors can send it (POST reports/executive-summary/send)
      canSend: user.type === "shis" || String(user.user_role) === "1" || ["executive secretary", "director"].includes(String(user.job_title || "").trim().toLowerCase()),
      preset: this.$route.query.from ? "custom" : "last_week",
      from: this.$route.query.from || "",
      to: this.$route.query.to || "",
      s: null,
      loading: false,
      sending: false,
      error: "",
      facilitySearch: "",
      claimStages: [
        { key: "vetted", label: "Vetted" },
        { key: "verified", label: "Verified" },
        { key: "approved", label: "Approved" },
        { key: "rejected", label: "Rejected" },
      ],
    };
  },
  computed: {
    e() { return this.s.enrolment.totals; },
    a() { return this.s.authorization_codes; },
    f() { return this.s.facilities; },
    facilityRows() {
      const q = this.facilitySearch.trim().toLowerCase();
      return q ? this.f.rows.filter((r) => (r.name || "").toLowerCase().includes(q)) : this.f.rows;
    },
    tiles() {
      const c = this.s.claims;
      return [
        { label: "Enrollees", value: this.e.total.toLocaleString(), sub: `+${this.e.new_total.toLocaleString()} in the period · ${this.e.principals.toLocaleString()} principals, ${this.e.dependants.toLocaleString()} dependants`, icon: "fe fe-users", bg: "#eff6ff", color: "#2563eb" },
        { label: "Claims submitted", value: c.submitted.count.toLocaleString(), sub: this.money(c.submitted.amount), icon: "fe fe-file-text", bg: "#fff7ed", color: "#ea580c" },
        { label: "Claims approved", value: c.approved.count.toLocaleString(), sub: this.money(c.approved.amount), icon: "fe fe-check-circle", bg: "#ecfdf5", color: "#059669" },
        { label: "Authorization codes", value: this.a.requested.toLocaleString(), sub: `${this.a.approved} approved · ${this.a.rejected} declined · ${this.a.pending_now} pending`, icon: "fe fe-shield", bg: "#f5f3ff", color: "#7c3aed" },
        { label: "Tickets", value: this.s.tickets.raised.toLocaleString(), sub: `${this.s.tickets.resolved} resolved · ${this.s.tickets.open_now} open`, icon: "fe fe-message-square", bg: "#fdf2f8", color: "#db2777" },
        { label: "Facilities submitting", value: `${this.f.facilities_submitting} / ${this.f.facilities_total}`, sub: `${this.f.totals.encounters} encounters · ${this.f.totals.quality_assurance_reports} QA reports`, icon: "fe fe-home", bg: "#f0fdfa", color: "#0d9488" },
      ];
    },
  },
  mounted() {
    if (this.preset !== "custom") this.applyPreset();
    else this.load();
  },
  methods: {
    applyPreset() {
      const monday = moment().startOf("isoWeek");
      if (this.preset === "last_week") {
        this.from = monday.clone().subtract(7, "days").format("YYYY-MM-DD");
        this.to = monday.clone().subtract(1, "days").format("YYYY-MM-DD");
      } else if (this.preset === "this_week") {
        this.from = monday.format("YYYY-MM-DD");
        this.to = moment().format("YYYY-MM-DD");
      } else if (this.preset === "last_month") {
        this.from = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
        this.to = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      }
      if (this.preset !== "custom") this.load();
    },
    load() {
      this.loading = true;
      this.error = "";
      this.axios
        .get("/api/v1/auth/reports/executive-summary", { params: { from: this.from || undefined, to: this.to || undefined } })
        .then((res) => { this.s = res.data.data; })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load the summary."; })
        .finally(() => { this.loading = false; });
    },
    send() {
      if (!confirm(`Email and message this summary (${this.from} to ${this.to}) to the Executive Secretary and Directors now?`)) return;
      this.sending = true;
      this.axios
        .post("/api/v1/auth/reports/executive-summary/send", { from: this.from, to: this.to })
        .then((res) => this.$toasted.success(res.data.message, { position: "top-center", duration: 4000 }))
        .catch((e) => this.$toasted.error((e.response && e.response.data && e.response.data.message) || "Could not send.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.sending = false; });
    },
    money(v) {
      return "₦" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    minutes(m) {
      if (m < 60) return `${m} min`;
      if (m < 60 * 48) return `${(m / 60).toFixed(1)} h`;
      return `${(m / 1440).toFixed(1)} days`;
    },
  },
};
</script>

<style scoped>
@import "./reports.css";
</style>
